import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '../services/toastService'
import { entityAPI, sightingAPI, uploadAPI } from '../services/api'
import { trackSightingCreated, trackSpotModeActivate, trackSpotModeValidationFailed, trackSpotModeSubmitAttempt, trackSpotModeSubmitFailed, trackActivation, trackSpotModeAuthGate, trackSpotModeImageNsfwRejected } from '../utils/analytics'
import { createLogger } from '../utils/logger'
import useOnboardingStepComplete from './useOnboardingStepComplete'
import { saveDraft } from '../utils/spotDraftStorage'

const logger = createLogger('useSpotMode')

/**
 * Custom hook for spot mode functionality
 * Handles spot mode activation and sighting submission
 */
export function useSpotMode({
  user,
  activateSpotMode,
  deactivateSpotMode,
  spotModeEntity,
  spotModeLocation,
  spotModeNotes,
  spotModeEntityStatus = null,
  spotModeImages = [],
  spotModeSocials = {},
  spotModeEntityType = 'food_truck',
  setIsSubmittingSpot,
  spotModeStartTime = null,
  setSpotModeStartTime = null,
  spotModeLocationSetTime = null,
  setSpotModeLocationSetTime = null,
  userLocation,
  radius,
  fetchNearbyEntities,
  setEntities,
  spotModeCallback,
  isImmersiveMode = false,
  onTabChange = null,
  setPendingShareSighting = null,
  setPendingAuthSubmit = null,
  setIsSpotPanelMinimized = null,
  getUserLocation = null,
  t
}) {
  const navigate = useNavigate()
  const completeOnboardingStep = useOnboardingStepComplete()
  
  // Helper to get duration since activation
  const getActivationDuration = () => {
    if (!spotModeStartTime) return 0
    return Math.floor((Date.now() - spotModeStartTime) / 1000)
  }
  
  // Helper to get duration since location set
  const getLocationDuration = () => {
    if (!spotModeLocationSetTime) return 0
    return Math.floor((Date.now() - spotModeLocationSetTime) / 1000)
  }

  // Activation: open spot mode for any user — auth check deferred to submit time
  const handleSpotModeActivation = useCallback((prefilledEntity = null, prefilledLocation = null) => {
    // Use prefilled entity and location
    activateSpotMode(null, prefilledEntity, prefilledLocation)
    
    // Request GPS only when we don't already have a location.
    // If userLocation is already known, useSpotModeEffects will use it directly.
    // Calling getUserLocation() when a location is already set re-centers the map
    // and disrupts flows like "Spot it here" from a popup.
    if (!prefilledLocation && !userLocation && getUserLocation) {
      getUserLocation()
    }

    // Track activation
    const pageContext = isImmersiveMode ? 'immersive' : 'standard'
    trackSpotModeActivate('button', !!prefilledEntity, !!prefilledLocation, pageContext)
    
    // Set start time for analytics
    if (setSpotModeStartTime) {
      setSpotModeStartTime(Date.now())
    }
  }, [user, navigate, activateSpotMode, isImmersiveMode, onTabChange, setSpotModeStartTime, getUserLocation, t])

  const handleSpotSubmit = useCallback(async () => {
    const duration = getActivationDuration()

    // Auth check at submit time — save draft before deactivating so cleanup effect doesn't wipe it
    if (!user) {
      const draft = {
        entity: spotModeEntity,
        location: spotModeLocation,
        notes: spotModeNotes,
        images: spotModeImages, // File objects — persisted via IndexedDB in saveDraft
        entityType: spotModeEntityType
      }
      if (setPendingAuthSubmit) setPendingAuthSubmit(draft)
      // Also persist to sessionStorage + IndexedDB for OAuth redirect survival
      saveDraft(draft)
      // Track auth gate before deactivating
      trackSpotModeAuthGate(!!spotModeEntity, !!spotModeLocation, spotModeImages.length > 0, spotModeEntityType)
      // Deactivate spot mode so the immersive panel becomes visible (it is hidden while isSpotModeActive)
      deactivateSpotMode()
      if (isImmersiveMode && onTabChange) onTabChange('auth', 'expanded')
      else navigate('/login', { state: { from: window.location.pathname } })
      return false
    }

    if (!spotModeEntity || !spotModeLocation) {
      const reason = !spotModeEntity ? 'missing_entity' : 'missing_location'
      trackSpotModeValidationFailed(reason, !!spotModeEntity, !!spotModeLocation, spotModeImages?.length || 0, duration)
      toast.error(t ? t('map.enterEntityAndLocation') : 'Please enter an entity name and location')
      return false
    }

    if (!spotModeImages || spotModeImages.length === 0) {
      trackSpotModeValidationFailed('missing_images', !!spotModeEntity, !!spotModeLocation, 0, duration)
      toast.error(t ? t('map.imagesRequired', { max: 5 }) : 'Please add at least one image (1-5 required)')
      return false
    }

    if (spotModeImages.length > 5) {
      trackSpotModeValidationFailed('too_many_images', !!spotModeEntity, !!spotModeLocation, spotModeImages.length, duration)
      toast.error(t ? t('map.maxImagesExceeded', { max: 5 }) : 'Maximum 5 images allowed')
      return false
    }

    // Track submit attempt
    const isNewEntity = !spotModeEntity.id
    trackSpotModeSubmitAttempt(
      spotModeEntity.id || 'new',
      isNewEntity,
      spotModeEntityType,
      !!spotModeNotes,
      spotModeImages.length,
      duration,
      getLocationDuration()
    )
    
    setIsSubmittingSpot(true)
    let shouldNavigateToEntity = null
    try {
      let entityId = spotModeEntity.id

      // Upload images FIRST — before creating any entity.
      // This prevents orphan entities when the upload fails (e.g. file too large).
      // If the upload fails here the user can try again without a duplicate entity being created.
      const uploadPromises = spotModeImages.map(file => uploadAPI.uploadImage(file))
      const uploadResults = await Promise.all(uploadPromises)
      const imageUrls = uploadResults.map(result => result.data.url)
      // safeSearchPassed: true when the backend NSFW scan cleared the image.
      // Passed images are auto-approved; others go to the admin moderation queue.
      const imagesSafeSearch = uploadResults.map(result => result.data.safeSearchPassed === true)

      // If spotModeEntity doesn't have an id, it means user typed a new name
      // Create the entity only after images are safely uploaded
      if (!entityId) {
        const entityName = (spotModeEntity.name || '').trim()
        if (!entityName) {
          toast.error(t ? t('map.spotMode.entityNameRequired', 'Please enter a name for this location') : 'Please enter a name for this location')
          return
        }

        // Map entity_type to valid category (food_truck, ice_cream, coffee, retail, service, entertainment, other)
        const getCategoryFromEntityType = (entityType) => {
          const categoryMap = {
            'food_truck': 'food_truck',
            'restaurant': 'food_truck', // Fixed location food venue maps to food_truck
            'pop_up': 'food_truck', // Pop-ups are typically food-related
            'market': 'retail', // Markets map to retail
            'delivery': 'food_truck', // Delivery-only maps to food_truck
            'catering': 'service', // Catering is a service
            'other': 'other'
          }
          return categoryMap[entityType] || 'other'
        }

        const newEntityResponse = await entityAPI.createEntity({
          name: entityName,
          entity_type: spotModeEntityType,
          category: getCategoryFromEntityType(spotModeEntityType),
          description: spotModeNotes ? `Spotted via map: ${spotModeNotes}` : 'Added via spot mode',
          ...((spotModeSocials?.instagram_url) && { instagram_url: spotModeSocials.instagram_url }),
          ...((spotModeSocials?.tiktok_url)    && { tiktok_url:    spotModeSocials.tiktok_url    }),
          ...((spotModeSocials?.facebook_url)  && { facebook_url:  spotModeSocials.facebook_url  }),
          ...((spotModeSocials?.x_url)         && { x_url:         spotModeSocials.x_url          }),
        })
        entityId = newEntityResponse.data.id
        // Mark spotted immediately after entity creation so a subsequent
        // sighting-API failure cannot prevent this step from completing.
        completeOnboardingStep('spotted')
        toast.success(t ? t('map.entityCreated', { name: spotModeEntity.name }) : `Created new entry: ${spotModeEntity.name}`)
        // Navigate to the new entity's detail page after the sighting is created below
        shouldNavigateToEntity = entityId
      }

      // Create the sighting with already-uploaded image URLs
      const sightingResponse = await sightingAPI.createSighting({
        entityId: entityId,
        latitude: spotModeLocation.lat,
        longitude: spotModeLocation.lng,
        notes: spotModeNotes || undefined,
        sightingType: 'tag',
        images: imageUrls,
        imagesSafeSearch,  // parallel array: true = auto-approved, false = admin review needed
        ...(spotModeEntityStatus ? { entity_status: spotModeEntityStatus } : {})
      })

      trackSightingCreated(entityId)
      trackActivation('first_sighting', { entity_id: entityId })
      completeOnboardingStep('spotted')
      toast.success(t ? t('map.spotSuccess', { name: spotModeEntity.name }) : `Successfully spotted ${spotModeEntity.name}!`)

      // Post-sighting nudge: encourage leaving a message in the entity's channel
      setTimeout(() => {
        toast.info(t ? t('map.reviewNudge') : 'Know more about this vendor? Join their channel and leave a message!')
      }, 2500)

      // Streak toast: show if user has 3+ distinct sighting days in last 14 days
      const streakDays = sightingResponse?.data?.streak_days
      if (streakDays >= 3) {
        setTimeout(() => {
          toast.success(t ? t('map.streakToast', { count: streakDays }) : `${streakDays}-day sighting streak!`)
        }, 5000)
      }

      // Trigger share card before deactivating
      const newSightingId = sightingResponse?.data?.sighting?.id
      if (setPendingShareSighting && newSightingId) {
        setPendingShareSighting({ sightingId: newSightingId, entityName: spotModeEntity?.name || '' })
      }

      // Store the current location and radius before deactivating spot mode
      const currentUserLocation = userLocation
      const currentRadius = radius
      const callback = spotModeCallback

      // Deactivate spot mode
      deactivateSpotMode()

      // Navigate to the new entity's detail page (only for newly created entities)
      if (shouldNavigateToEntity) {
        navigate(`/entity/${shouldNavigateToEntity}`)
        return true
      }

      // Immediately refresh the map with cache-busting
      if (currentUserLocation && fetchNearbyEntities) {
        if (setEntities) setEntities([]) // Clear first to show loading state
        await fetchNearbyEntities(currentUserLocation, currentRadius, true)
      }

      // Call callback if provided
      if (callback) {
        callback()
      }
      return true
    } catch (error) {
      logger.error('Failed to create sighting', error)
      
      // Log the full error response for debugging
      if (error.response?.data) {
        logger.error('Backend error response:', error.response.data)
      }

      // NSFW rejection (422 Unprocessable Entity from upload step)
      if (error.response?.status === 422) {
        const file = spotModeImages?.[0]
        trackSpotModeImageNsfwRejected(
          file?.type || 'unknown',
          file ? Math.round(file.size / 1024) : 0,
          spotModeImages?.length || 0
        )
      }
      
      // Track failure
      const errorType = error.response ? 'server' : (error.request ? 'network' : 'unknown')
      trackSpotModeSubmitFailed(
        error.message || 'Unknown error',
        errorType,
        isNewEntity,
        duration
      )
      
      // Show specific error message if available
      const errorMessage = error.response?.data?.error || error.response?.data?.message || (t ? t('map.spotFailed') : 'Failed to create sighting. Please try again.')
      toast.error(errorMessage)
      return false
    } finally {
      setIsSubmittingSpot(false)
    }
  }, [
    user,
    navigate,
    spotModeEntity,
    spotModeLocation,
    spotModeNotes,
    spotModeImages,
    spotModeEntityType,
    setIsSubmittingSpot,
    deactivateSpotMode,
    spotModeStartTime,
    spotModeLocationSetTime,
    userLocation,
    radius,
    fetchNearbyEntities,
    setEntities,
    spotModeCallback,
    isImmersiveMode,
    onTabChange,
    setPendingShareSighting,
    setPendingAuthSubmit,
    setIsSpotPanelMinimized,
    deactivateSpotMode,
    t
  ])

  return {
    handleSpotModeActivation,
    handleSpotSubmit
  }
}
